import { useGetFareFamilyInfoQuery } from '@/src/api/rtkQuery/fareFamilyApi'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { Fare } from '@/src/types/itinerary'
import { createJourneyTabs } from '@/src/utils/itinerary/fareFamily'
import { useLocale } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'
import useClevertapForItinerary from '../../analytics/clevertap/itinerary'
import useFormatCurrencyForItinerary from '../../currency/useFormatCurrencyForItinerary'
import useGetItineraryId from '../useGetItineraryId'
import useBenefitProcessor from './useBenefitProcessor'
import useFareRuleProcessor from './useFareRuleProcessor'

const useTransparencyWidget = () => {
    // State management
    const [tabValue, setTabValue] = useState<0 | 1>(0)
    const [fareRuleExpanded, setFareRuleExpanded] = useState<number[]>([])
    const [showFareFamilyCards, setShowFareFamilyCards] = useState(false)

    // Hooks and utilities
    const { triggerItineraryEvent } = useClevertapForItinerary()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const itineraryId = useGetItineraryId()
    const { formatDate } = useFormatDate()
    const locale = useLocale()

    // API calls
    const {
        meta: { fareFamilyEnabledOnReview },
        fares
    } = useGetItineraryDataQuery(
        {
            itineraryId
        },
        {
            selectFromResult: ({ data }) => ({
                meta: data?.meta || {},
                fares: data?.fareFamilyFares || []
            })
        }
    )

    const {
        data: fareFamilyInfoData,
        isError,
        isLoading: isFareFamilyInfoLoading,
        isFetching: isFareFamilyInfoFetching
    } = useGetFareFamilyInfoQuery(
        {
            itineraryId
        },
        {
            skip: !fareFamilyEnabledOnReview
        }
    )

    // Derived state
    const selectedFare = useMemo(() => fares.find(fare => fare.selected), [fares])
    const isLoading = isFareFamilyInfoLoading || isFareFamilyInfoFetching
    const showEditFares = fares?.length > 1

    // Helper hooks
    const { processBenefits } = useBenefitProcessor({ locale })
    const { processedFareRules } = useFareRuleProcessor({
        fareFamilyInfoData,
        tabValue,
        selectedFare,
        formatDate,
        formatCurrency
    })

    // Event handlers
    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: 0 | 1) => {
        setTabValue(newValue)
    }, [])

    const handleFareRuleExpanded = (index: number, type: string) => {
        setFareRuleExpanded(prev => {
            if (prev.includes(index)) {
                triggerItineraryEvent('Air_UI_Action', {
                    action_name: 'fare_family_rule_collapsed',
                    action_type: type
                })
                return prev.filter(i => i !== index)
            }
            triggerItineraryEvent('Air_UI_Action', {
                action_name: 'fare_family_rule_expanded',
                action_type: type
            })
            return [...prev, index]
        })
    }

    const closeFareFamilyModal = () => {
        setShowFareFamilyCards(false)
    }

    const openFareFamilyModal = () => {
        triggerItineraryEvent('Air_UI_Action', {
            action_name: 'edit_fare_clicked',
            action_type: 'BUTTON'
        })
        setShowFareFamilyCards(true)
    }

    // Computed values
    const journeyTabs = useMemo(() => {
        const solutions = fareFamilyInfoData?.data?.solutions || []
        return createJourneyTabs(solutions, selectedFare as Fare)
    }, [fareFamilyInfoData, selectedFare])

    const benefits = useMemo(() => {
        const fareBenefits = selectedFare?.fareBenefits
        if (!fareBenefits) return []
        return processBenefits(fareBenefits)
    }, [selectedFare, processBenefits])

    return {
        fareFamilyEnabledOnReview,
        tabValue,
        isLoading,
        isError,
        journeyTabs,
        benefits,
        fareRuleExpanded,
        processedFareRules,
        showFareFamilyCards,
        showEditFares,
        selectedFare,
        handleFareRuleExpanded,
        handleTabChange,
        openFareFamilyModal,
        closeFareFamilyModal
    }
}

export default useTransparencyWidget
